import ShippingSummary from '../Shipping/ShippingSummary';
import parseOrderMetadata from '../Project/projectMetadata';
import { nullSafeToString, toDisplayPrice } from '../lib/util';

/*
 * The API flattens an order's billing state and country onto it as bare id/name pairs, with no
 * abbreviations - those live on the dedicated billingAddress endpoint. See OrderBillingAddress.
 */

function toAddressRegion (region) {
    if (!region) {
        return null;
    }

    return Object.freeze({
        id: region.id,
        name: nullSafeToString(region.name),
    });
}

/**
 * A submitted order, as returned by {@link Collaterate.Checkout.submit Collaterate.Checkout.submit} and
 * by the {@link Collaterate.Order Collaterate.Order} lookups.
 *
 * Note on payment: card and PayPal orders are **authorized, not captured**, at submit time. A returned
 * Order means funds were held, not settled - capture happens later in Collaterate. Do not present a
 * returned Order to a customer as a completed payment.
 *
 * Note on totals: every top-level money field is the order **as submitted**, and never changes. An order
 * that was adjusted afterwards - a credit, a quantity change, a return - keeps those original figures
 * and reports the current ones on `adjustedTotals`. Render `adjustedTotals` anywhere a shopper is being
 * shown what an order is worth now; an order straight out of checkout has the two in agreement.
 *
 * @property {number} id - (Read only) This order's unique id.
 * @property {number} number - (Read only) The customer-facing order number.
 * @property {string} status - (Read only) The order's current status.
 * @property {?number} statusId - (Read only) The id of the order's current status.
 * @property {Date} createdOn - (Read only) When the order was created.
 * @property {?Date} shippedOn - (Read only) When the order shipped. Null if it has not shipped.
 * @property {string} projectName - (Read only) The name of the project this order was created from.
 * @property {string} firstName - (Read only) First name of the user who placed the order.
 * @property {string} lastName - (Read only) Last name of the user who placed the order.
 * @property {string} email - (Read only) Email of the user who placed the order.
 * @property {string} phone - (Read only) Phone of the user who placed the order.
 * @property {?number} userId - (Read only) Id of the user who placed the order.
 * @property {string} po - (Read only) The purchase order number, if one was supplied.
 * @property {string} paymentType - (Read only) The payment type used, e.g. "TERMS", "CREDIT_CARD", "PAYPAL".
 * @property {string} paymentTypeDescription - (Read only) A human-readable label for the payment type.
 * @property {string} paymentProviderType - (Read only) The payment provider used, e.g. "BRAINTREE".
 * @property {Collaterate.Order~billingAddress} billingAddress - (Read only) The billing address recorded on the order.
 * @property {?ShippingSummary} shippingSummary - (Read only) A rollup of this order's shipping: the first shipment's destination, priced across all shipments. Null when no shipping service was selected. Carries no street address - use {@link Collaterate.Order.getShipments Collaterate.Order.getShipments} for that.
 * @property {boolean} isLive - (Read only) Whether this is a live order rather than a test one.
 * @property {boolean} isClosed - (Read only) Whether this order is closed.
 * @property {boolean} requiresApproval - (Read only) Whether this order requires approval before production.
 * @property {boolean} isApproved - (Read only) Whether this order has been approved.
 * @property {string} approvalReasons - (Read only) Why this order requires approval.
 * @property {number} subtotalPrice - (Read only) Total price of all items, before handling, shipping, tax and discounts.
 * @property {string} displaySubtotalPrice - (Read only) this.subtotalPrice formatted with commas and two decimal places.
 * @property {number} handlingPrice - (Read only) Total handling price for this order.
 * @property {string} displayHandlingPrice - (Read only) this.handlingPrice formatted with commas and two decimal places.
 * @property {number} shipmentPrice - (Read only) Total shipping price for this order.
 * @property {string} displayShipmentPrice - (Read only) this.shipmentPrice formatted with commas and two decimal places.
 * @property {number} taxPrice - (Read only) Tax charged on this order.
 * @property {string} displayTaxPrice - (Read only) this.taxPrice formatted with commas and two decimal places.
 * @property {number} discountPrice - (Read only) Total discounts applied to this order.
 * @property {string} displayDiscountPrice - (Read only) this.discountPrice formatted with commas and two decimal places.
 * @property {number} creditAccountsDiscountAmount - (Read only) Amount covered by store credit.
 * @property {string} displayCreditAccountsDiscountAmount - (Read only) this.creditAccountsDiscountAmount formatted with commas and two decimal places.
 * @property {number} totalPrice - (Read only) The final price for this order.
 * @property {string} displayTotalPrice - (Read only) this.totalPrice formatted with commas and two decimal places.
 * @property {Order~adjustedTotals} adjustedTotals - (Read only) The same totals with post-order adjustments applied. Equal to the top-level figures for an unadjusted order.
 * @property {string} promotionName - (Read only) The name of the promotion used on this order. Empty if none was.
 * @property {string} externalId - (Read only) This order's id in an external system, if it came from one.
 * @property {boolean} hasSalesEstimate - (Read only) Whether this order came from a custom quote.
 * @property {Object} orderMetadata - (Read only) The shopper's answers to this storefront's order metadata questions, keyed by question label. Same shape as {@link Project#orderMetadata Project#orderMetadata} and {@link Checkout#orderMetadata Checkout#orderMetadata}. Empty when the storefront collects none, or the order predates them.
 */

class Order {
    constructor (data = {}) {
        this.id = data.id;
        this.number = data.number;
        this.status = nullSafeToString(data.status);
        this.statusId = data.statusId;
        this.createdOn = data.createdOn ? Object.freeze(new Date(data.createdOn)) : null;
        this.shippedOn = data.shippedOn ? Object.freeze(new Date(data.shippedOn)) : null;
        this.projectName = nullSafeToString(data.projectName);

        this.firstName = nullSafeToString(data.fname);
        this.lastName = nullSafeToString(data.lname);
        this.email = nullSafeToString(data.email);
        this.phone = nullSafeToString(data.phone);
        this.userId = data.userId;

        this.po = nullSafeToString(data.po);
        this.paymentType = nullSafeToString(data.checkoutPaymentType);
        this.paymentTypeDescription = nullSafeToString(data.checkoutPaymentTypeDescription);
        this.paymentProviderType = nullSafeToString(data.paymentProviderType);

        this.billingAddress = Object.freeze({
            firstName: nullSafeToString(data.billingFname),
            lastName: nullSafeToString(data.billingLname),
            company: nullSafeToString(data.billingCompany),
            address: nullSafeToString(data.billingAddress),
            address2: nullSafeToString(data.billingAddress2),
            city: nullSafeToString(data.billingCity),
            postalCode: nullSafeToString(data.billingZip),
            state: toAddressRegion(data.billingState),
            country: toAddressRegion(data.billingCountry),
        });

        this.shippingSummary = data.shippingSummary ? new ShippingSummary(data.shippingSummary) : null;

        this.isLive = Boolean(data.live);
        this.isClosed = Boolean(data.closed);
        this.requiresApproval = Boolean(data.requiresApproval);
        this.isApproved = Boolean(data.approved);
        this.approvalReasons = nullSafeToString(data.approvalReasons);

        this.subtotalPrice = data.totalItemPrice || 0;
        this.displaySubtotalPrice = toDisplayPrice(this.subtotalPrice);
        this.handlingPrice = data.totalHandlingPrice || 0;
        this.displayHandlingPrice = toDisplayPrice(this.handlingPrice);
        this.shipmentPrice = data.totalShipmentPrice || 0;
        this.displayShipmentPrice = toDisplayPrice(this.shipmentPrice);
        this.taxPrice = data.taxPrice || 0;
        this.displayTaxPrice = toDisplayPrice(this.taxPrice);
        this.discountPrice = data.totalDiscountPrice || 0;
        this.displayDiscountPrice = toDisplayPrice(this.discountPrice);
        this.creditAccountsDiscountAmount = data.totalCreditAccountsDiscount || 0;
        this.displayCreditAccountsDiscountAmount = toDisplayPrice(this.creditAccountsDiscountAmount);
        this.totalPrice = data.totalPrice || 0;
        this.displayTotalPrice = toDisplayPrice(this.totalPrice);

        /*
         * The API sends the *AfterAdjustments figures alongside the originals on every order, adjusted
         * or not, so these need no fallback to the top-level values - they already carry them.
         */
        this.adjustedTotals = Object.freeze({
            subtotalPrice: data.totalItemPriceAfterAdjustments || 0,
            displaySubtotalPrice: toDisplayPrice(data.totalItemPriceAfterAdjustments),
            handlingPrice: data.totalHandlingPriceAfterAdjustments || 0,
            displayHandlingPrice: toDisplayPrice(data.totalHandlingPriceAfterAdjustments),
            shipmentPrice: data.totalShipmentPriceAfterAdjustments || 0,
            displayShipmentPrice: toDisplayPrice(data.totalShipmentPriceAfterAdjustments),
            taxPrice: data.taxPriceAfterAdjustments || 0,
            displayTaxPrice: toDisplayPrice(data.taxPriceAfterAdjustments),
            discountPrice: data.totalDiscountPriceAfterAdjustments || 0,
            displayDiscountPrice: toDisplayPrice(data.totalDiscountPriceAfterAdjustments),
            creditAccountsDiscountAmount: data.totalCreditAccountsDiscountAfterAdjustments || 0,
            displayCreditAccountsDiscountAmount: toDisplayPrice(data.totalCreditAccountsDiscountAfterAdjustments),
            totalPrice: data.totalPriceAfterAdjustments || 0,
            displayTotalPrice: toDisplayPrice(data.totalPriceAfterAdjustments),
        });

        this.promotionName = nullSafeToString(data.promotionName);
        this.externalId = nullSafeToString(data.externalId);
        this.hasSalesEstimate = Boolean(data.hasSalesEstimate);

        /*
         * The order carries its own copy of the answers, snapshotted from the project at submit
         * (OrderService seeds it, OrderUtils#applyMetaData maintains it), in the same index-keyed JSON
         * string the project uses - so the project's parser reads it unchanged.
         */
        this.orderMetadata = parseOrderMetadata(data.metadata);

        Object.freeze(this);
    }
}

export default Order;

/**
 * The billing address recorded on an order.
 *
 * @typedef {Object} Collaterate.Order~billingAddress
 * @property {string} firstName - First name on the billing address.
 * @property {string} lastName - Last name on the billing address.
 * @property {string} company - Company on the billing address.
 * @property {string} address - Street address.
 * @property {string} address2 - Second street address line.
 * @property {string} city - City.
 * @property {string} postalCode - Postal code.
 * @property {?Collaterate.Order~addressRegion} state - The state (region), as an id and name only. Null for countries without them.
 * @property {?Collaterate.Order~addressRegion} country - The country, as an id and name only. Null if the order has none.
 */

/**
 * A state or country on an order's flattened billing address. Use
 * {@link Collaterate.Order.getBillingAddress Collaterate.Order.getBillingAddress} when the abbreviation
 * is needed too.
 *
 * @typedef {Object} Collaterate.Order~addressRegion
 * @property {number} id - The region's unique id.
 * @property {string} name - The region's name.
 */

/**
 * An order's totals with post-order adjustments applied. Shaped exactly like the order's own top-level
 * money fields, so the two are interchangeable at a render site.
 *
 * @typedef {Object} Order~adjustedTotals
 * @property {number} subtotalPrice - Total price of all items, before handling, shipping, tax and discounts.
 * @property {string} displaySubtotalPrice - subtotalPrice formatted with commas and two decimal places.
 * @property {number} handlingPrice - Total handling price.
 * @property {string} displayHandlingPrice - handlingPrice formatted with commas and two decimal places.
 * @property {number} shipmentPrice - Total shipping price.
 * @property {string} displayShipmentPrice - shipmentPrice formatted with commas and two decimal places.
 * @property {number} taxPrice - Tax charged.
 * @property {string} displayTaxPrice - taxPrice formatted with commas and two decimal places.
 * @property {number} discountPrice - Total discounts applied.
 * @property {string} displayDiscountPrice - discountPrice formatted with commas and two decimal places.
 * @property {number} creditAccountsDiscountAmount - Amount covered by store credit.
 * @property {string} displayCreditAccountsDiscountAmount - creditAccountsDiscountAmount formatted with commas and two decimal places.
 * @property {number} totalPrice - The final price.
 * @property {string} displayTotalPrice - totalPrice formatted with commas and two decimal places.
 */
